import { useEffect, useReducer, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

const transitionTime = 400;
const threshold = 0.2;
const limit = 1.2;
const smooth = `transform ${transitionTime}ms ease`;
const none = `none`;

export const getPreviousIndex = (length, current) => {
  return (current - 1 + length) % length;
};

export const getNextIndex = (length, current) => {
  return (current + 1) % length;
};

const carouselReducer = (state, action) => {
  let index = 0;
  switch (action.type) {
    case 'jump':
      if (state.desired !== action.desired) {
        return {
          ...state,
          smooth: action.smooth,
          desired: action.desired,
        };
      }
      return state;
    case 'next':
      index = getNextIndex(action.length, state.active);
      return {
        ...state,
        nextWasLast: true,
        desired: index,
      };
    case 'prev':
      index = getPreviousIndex(action.length, state.active);
      return {
        ...state,
        nextWasLast: false,
        desired: index,
      };
    case 'done':
      return {
        ...state,
        offset: NaN,
        active: state.desired,
        smooth: true,
      };
    case 'drag':
      return {
        ...state,
        offset: action.offset,
      };
    default:
      return state;
  }
};

const swiped = (delta, dispatch, length, dir, container) => {
  const t = container.clientWidth * threshold;
  const d = Math.abs(delta);

  if (d >= t) {
    dispatch(dir > 0 ? { type: 'next', length } : { type: 'prev', length });
  } else {
    dispatch({
      type: 'drag',
      offset: 0,
    });
  }
};

const useCompareCarousel = (
  length,
  isStatic,
  onSlideChange = null,
  initialCarouselState = {
    offset: 0,
    desired: 0,
    active: 0,
    nextWasLast: true,
    smooth: true,
  }
) => {
  const [state, dispatch] = useReducer(carouselReducer, initialCarouselState);
  const [container, setContainer] = useState(null);
  const { ref, onMouseDown } = useSwipeable({
    onSwiping(e) {
      if (e.initial[0] !== e.initial[1]) {
        const sign = e.deltaX > 0 ? 1 : -1;
        dispatch({
          type: 'drag',
          offset:
            sign * Math.min(Math.abs(e.deltaX), limit * container.clientWidth),
        });
      }
    },
    onSwipedLeft(e) {
      if (e.initial[0] !== e.initial[1]) {
        swiped(e.deltaX, dispatch, length, 1, container);
      }
    },
    onSwipedRight(e) {
      if (e.initial[0] !== e.initial[1]) {
        swiped(e.deltaX, dispatch, length, -1, container);
      }
    },
    trackMouse: !isStatic,
    trackTouch: !isStatic,
  });

  const handlers = {
    onMouseDown,
    ref(container) {
      setContainer(container && container.firstElementChild);
      return ref(container);
    },
  };

  useEffect(() => {
    if (onSlideChange) {
      onSlideChange(state.desired, state.nextWasLast);
    }
    const id = setTimeout(() => dispatch({ type: 'done' }), transitionTime);
    return () => clearTimeout(id);
  }, [state.desired]);

  const style = {
    transform: 'translateX(0)',
    width: `${100 * (length + 2)}%`,
    left: `-${(state.active + 1) * 100}%`,
  };

  if (state.desired !== state.active) {
    const shift = (100 * (state.nextWasLast ? -1 : 1)) / (length + 2);
    style.transition = state.smooth ? smooth : none;
    style.transform = `translateX(${shift}%)`;
  } else if (!isNaN(state.offset)) {
    if (state.offset !== 0) {
      style.transform = `translateX(${state.offset}px)`;
    } else {
      style.transition = smooth;
    }
  }

  return [
    handlers,
    style,
    () => dispatch({ type: 'next', length }),
    () => dispatch({ type: 'prev', length }),
    (desired, smooth = true) => dispatch({ type: 'jump', desired, smooth }),
  ];
};

export default useCompareCarousel;
